import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, catchError, forkJoin, map, of, takeUntil } from 'rxjs';
import {
  AgreementTravelFormGroup,
  AgreementTravelFormOutput,
} from 'src/app/pages/agreements/types/agreement-create-update-form.model';
import { GetDefaultRatesQueryResult } from 'src/app/shared/graphql/agreements/default-rates-query.graphql';
import { GetDefaultRatesQueryService } from 'src/app/shared/graphql/agreements/default-rates-query.graphql.autogenerated';
import { GetMableFeesQueryResult } from 'src/app/shared/graphql/agreements/mable-fees-query.graphql';
import { GetMableFeesQueryService } from 'src/app/shared/graphql/agreements/mable-fees-query.graphql.autogenerated';
import { Agreement } from 'src/app/shared/models/gql/agreement.model';

@Component({
  selector: 'app-agreement-travel-form',
  templateUrl: './agreement-travel-form.component.html',
  styleUrls: ['./agreement-travel-form.component.scss'],
})
export class AgreementTravelFormComponent implements OnInit, OnDestroy {
  /** Set the agreement travel rate form values from the agreement input  */
  @Input() set agreement(agreement: Agreement) {
    this.agreementData = agreement;

    this.travelForm.controls.includeTravelRate.setValue(agreement.travelExpenseFlag || null);
    this.travelForm.controls.perKmTravelRate.setValue(this.getPerKmRate());
  }

  /** The travel form change event emitter */
  @Output() valueChange = new EventEmitter<Partial<AgreementTravelFormOutput>>();

  /** The agreement data */
  agreementData: Agreement;

  /** Flag to check travel section is disabled or not */
  isTravelSectionDisabled = false;

  /** Mable fee charged on support providers */
  currentSupportProviderFee: number | string = '';

  /** Mable fee charged on clients */
  currentClientFee: number | string = '';

  /** The NDIA price guide travel rate */
  ndiaPriceGuideTravelRate: number | string;

  /** The default travel per km rate */
  defaultPerKmRate: number | string;

  /** The default travel per km net rate, that is, the rate after charging Mable fee */
  defaultPerKmNetRate: number | string;

  /** The destroy subject. Used to unsubscribe the value change subscription. */
  private destroySub$ = new Subject();

  /** Form group for travel expenses */
  travelForm = new FormGroup<AgreementTravelFormGroup>({
    includeTravelRate: new FormControl<boolean>(false),
    perKmTravelRate: new FormControl<string>('', [Validators.required, Validators.pattern(/^\d*(\.\d{1,2})*(,\d+)?$/)]),
  });

  constructor(
    private getDefaultRatesQueryService: GetDefaultRatesQueryService,
    private getMableFeesQueryService: GetMableFeesQueryService,
  ) {}

  ngOnInit() {
    this.setTravelSectionVisibility();
    this.includeTravelRateListener();
    this.setDefaultRatesAndMableFees();
  }

  ngOnDestroy(): void {
    this.destroySub$.next(null);
    this.destroySub$.complete();
  }

  /**
   * Validate per km rate and if it's not valid then set error on control.
   */
  async validatePerKmRate(): Promise<void> {
    // if the travel checkbox is unchecked, validate the perKmTravelRate value as null
    if (!this.travelForm.get('includeTravelRate').value && this.travelForm.controls.includeTravelRate.value === null) {
      return;
    }

    if (Number(this.travelForm.value.perKmTravelRate) < 0) {
      this.travelForm.controls.perKmTravelRate.setErrors({
        invalidPerKmRateErrorMessage: 'Kilometre rate can’t be negative.',
      });
      return;
    }

    if (Number(this.travelForm.value.perKmTravelRate) < 0.01) {
      this.travelForm.controls.perKmTravelRate.setErrors({
        invalidPerKmRateErrorMessage: 'Rate must be at least $0.01.',
      });
    }

    // emit the values to the parent page
    this.onTravelRateChange();
  }

  /**
   * Listen to when the include travel rate toggle changes.
   * Enables and disables the per kilometre rate field based on the toggle value.
   */
  private includeTravelRateListener(): void {
    this.travelForm.controls.includeTravelRate.valueChanges
      .pipe(takeUntil(this.destroySub$))
      .subscribe((value: boolean) => {
        if (value) {
          this.travelForm.controls.perKmTravelRate.enable();
          this.travelForm.get('perKmTravelRate').setValue(this.getPerKmRate());
        } else {
          this.travelForm.controls.perKmTravelRate.disable();
        }
        this.onTravelRateChange();
      });
  }

  /**
   * Set visibility of travel section.
   */
  private setTravelSectionVisibility(): void {
    this.isTravelSectionDisabled =
      this.agreementData.otherUser?.ndisAgencyManaged || this.agreementData.otherUser?.disablePerKmTravel;
  }

  /**
   * Get per km travel rate based on user type and travel flag.
   */
  private getPerKmRate(): string {
    if (this.agreementData.travelExpenseFlag) {
      return this.agreementData.perKmRateInDollars;
    }

    return this.defaultPerKmRate?.toString() || '0.00';
  }

  /**
   * Emit the travel rate change.
   */
  private onTravelRateChange(): void {
    this.valueChange.emit({
      ...this.travelForm.value,
      perKmTravelRateErrors: this.travelForm.controls.perKmTravelRate.errors,
    });
  }

  /**
   * Function responsible for defining variables related to Mable fees and travel rates.
   * In case there's an error retrieving data from the server, sets these variables as empty strings.
   */
  private setDefaultRatesAndMableFees(): void {
    forkJoin([this.getMableFees(), this.getDefaultRates()])
      .pipe(takeUntil(this.destroySub$))
      .subscribe(([mableFees, defaultRates]: [GetMableFeesQueryResult, GetDefaultRatesQueryResult]) => {
        this.currentClientFee = mableFees?.clientFeePercentage;
        this.currentSupportProviderFee = mableFees?.workerFeePercentage;
        this.defaultPerKmRate = defaultRates?.defaultPerKmRate || '';
        this.ndiaPriceGuideTravelRate = defaultRates?.ndiaPriceGuideTravelRate || '';

        this.defaultPerKmNetRate = (
          Number(this.defaultPerKmRate) *
          (1 - Number(this.currentSupportProviderFee) / 100)
        )?.toFixed(2);
      });
  }

  /**
   * Retrieves default per km rate and ndia price guide travel rate from the server.
   */
  private getDefaultRates(): Observable<GetDefaultRatesQueryResult> {
    return this.getDefaultRatesQueryService.fetch().pipe(
      map((response) => response.data.defaultRates),
      catchError((error) => {
        console.error('[AgreementTravelFormComponent] Error retrieving default rates', error);
        return of(null);
      }),
      takeUntil(this.destroySub$),
    );
  }

  /**
   * Retrieves Mable fees from the server.
   */
  private getMableFees(): Observable<GetMableFeesQueryResult> {
    return this.getMableFeesQueryService.fetch({ agreementId: String(this.agreementData.id) }).pipe(
      map((response) => response.data.mableFees),
      catchError((error) => {
        console.error('[AgreementTravelFormComponent] Error retrieving Mable fees', error);
        return of(null);
      }),
      takeUntil(this.destroySub$),
    );
  }
}
